import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {TabPanel, TabView} from 'primereact/tabview'
import {useCallback, useMemo} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {useQueryParams} from '../../utils/NavigatorUtils'
import {useAuth} from '../authentication/AuthProvider'
import {getSuperAdminRole} from '../authentication/AuthUtils'
import {detailHCPInformationList} from '../detail/DetailConstant'
import CustomDetailForm from '../form-utils/CustomDetailForm'
import PageContentWrapper from '../layout/PageContentWrapper'
import {treatAsRole} from '../patient/PatientConstants'
import {useRoleContext} from '../role/RoleProvider'
import {
    RoleActionType,
    RoleCategoryType,
    getAllowedPermissions,
} from '../role/RoleUtils'
import StyledHealthCarePractitionerManagementDatatable from '../styled-component/hc-practitioner/HealthCarePractitionerManagementDatatable'
import StyledHealthCarePractitionerRoleDatatable from '../styled-component/hc-practitioner/HealthCarePractitionerRoleDatatable'
import StyledHealthCarePractitionerStaticListDatatable from '../styled-component/hc-practitioner/HealthCarePractitionerStaticListDatatable'
import StyledHealthCarePractitionerSupportTeamDatatable from '../styled-component/hc-practitioner/HealthCarePractitionerSupportTeamDatatable'
import {
    useDetailUserWithRole,
    useHealthCareCRUD,
} from './query-keys/HealthCareQueryManager'

const USER_WITH_ROLE_PATH = 'userWithRole'

const tabView = {
    information: 'information',
    hcpRole: 'hcpRole',
    hcpPatient: 'hcpPatient',
    hcpStaticList: 'hcpStaticList',
    hcpSupportTeam: 'hcpSupportTeam',
}

const HealthCarePractitionerDetail = () => {
    useSignals()
    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {myRolePermissions} = useRoleContext()
    //#endregion

    //#region ID
    const {id: idString} = useParams()
    const id = useMemo(() => {
        if (!idString) {
            return null
        }
        return parseInt(idString)
    }, [idString])
    //#endregion

    //#region Query
    const queryClient = useQueryClient()
    //#endregion

    //#region Language
    const {translateKeys} = TranslateUtils()
    //#endregion

    //#region TabView
    const query = useQueryParams()
    const history = useHistory()
    //#endregion

    //#region Memo
    const getCurrentTabViewIndex = useMemo(() => {
        if (query && !!query?.get('tv')) {
            switch (query.get('tv')) {
                case tabView.information:
                    return 0
                case tabView.hcpRole:
                    return 1
                case tabView.hcpPatient:
                    return 2
                case tabView.hcpStaticList:
                    return 3
                case tabView.hcpSupportTeam:
                    return 4
                default:
                    return 0
            }
        }
    }, [query])
    //#endregion

    //#region State
    const currentTabView = useSignal(
        query?.get('tv') ? getCurrentTabViewIndex : 0,
    )
    //#endregion

    //#region Query
    const {healthCareKeys} = QueryKeyManager(QueryKeyTypes.hcp, user?.id)

    const hcpQueryKeys = useMemo(() => {
        return healthCareKeys.detail({id})
    }, [healthCareKeys, id])

    const {data, isFetched} = useDetailUserWithRole(
        user?.id,
        id,
        !!user?.id && !!id,
    )

    const {mutateAsync: createOrUpdateUserWithRole} = useHealthCareCRUD(
        QueryCRUDTypes.update,
        user?.id,
    )
    //#endregion

    //#region Handle Function
    const handleOnchange = e => {
        currentTabView.value = e.index
        history.push(
            `/hcp/${id}?tv=${
                e.index === 0
                    ? tabView.information
                    : e.index === 1
                    ? tabView.hcpRole
                    : e.index === 2
                    ? tabView.hcpPatient
                    : e.index === 3
                    ? tabView.hcpStaticList
                    : e.index === 4
                    ? tabView.hcpSupportTeam
                    : ''
            }`,
        )
    }
    const handleSubmit = useCallback(
        async data => {
            let toPostData = {
                ...data,
                id: data?.id ? data.id : null,
                byPassRole: true,
                byPassHospital: true,
            }

            const userWithRoleData = await createOrUpdateUserWithRole(
                toPostData,
            )

            if (userWithRoleData?.id) {
                queryClient.invalidateQueries({
                    queryKey: hcpQueryKeys,
                })
            }
        },
        [createOrUpdateUserWithRole, hcpQueryKeys, queryClient],
    )

    //#endregion

    //#region Event Listener
    // useEffect(() => {
    //     if (!!HCPRoleQueryKey && !!id) {
    //         detailEvent.on(
    //             'DETAIL-INFORMATION-RELOAD-' + HCPRoleQueryKey + id,
    //             handleUpdateHCPRoles,
    //         )

    //         return () => {
    //             removeDetailInformationReload(HCPRoleQueryKey + id)
    //         }
    //     }
    // }, [id, handleUpdateHCPRoles])
    //#endregion

    //#region Tab View Memo
    const dataListTabView = useMemo(() => {
        const defaultTabView = [
            {
                key: tabView.information,
                header: 'Information',
                component: () => (
                    <div className="grid pt-5">
                        {isFetched && (
                            <CustomDetailForm
                                initialData={data}
                                QueryKey={hcpQueryKeys}
                                PATH={USER_WITH_ROLE_PATH}
                                dataList={detailHCPInformationList}
                                treatAsRole={treatAsRole.HCP}
                                onSubmit={handleSubmit}
                            />
                        )}
                    </div>
                ),
            },
            {
                key: tabView.hcpRole,
                header: 'Hospitals and Roles',
                component: () => <StyledHealthCarePractitionerRoleDatatable />,
            },
            {
                key: tabView.hcpPatient,
                header: 'Patients',
                component: () => (
                    <StyledHealthCarePractitionerManagementDatatable />
                ),
            },
            {
                key: tabView.hcpStaticList,
                header: 'Static List',
                component: () => (
                    <StyledHealthCarePractitionerStaticListDatatable />
                ),
            },
            {
                key: tabView.hcpSupportTeam,
                header: 'Support Teams',
                component: () => (
                    <StyledHealthCarePractitionerSupportTeamDatatable />
                ),
            },
        ]

        if (getSuperAdminRole() || userRole === SystemRole.ADMIN) {
            return defaultTabView
        }

        const permissionHCPManagement = getAllowedPermissions(
            RoleCategoryType.hcpManagement,
            myRolePermissions,
        )

        const permissionChecks = {
            [tabView.information]:
                RoleActionType.hcpManagement.allowToViewHcpInfo,
            [tabView.hcpRole]: RoleActionType.hcpManagement.allowToViewHcpInfo,
            [tabView.hcpPatient]:
                RoleActionType.hcpManagement.allowToViewHcpPatient,
            [tabView.hcpStaticList]:
                RoleActionType.hcpManagement.allowToViewHcpStaticList,
            [tabView.hcpSupportTeam]:
                RoleActionType.hcpManagement.allowToViewHcpSupportTeams,
        }

        return defaultTabView.filter(item => {
            return permissionHCPManagement.includes(permissionChecks[item.key])
        })
    }, [
        userRole,
        myRolePermissions,
        isFetched,
        data,
        hcpQueryKeys,
        handleSubmit,
    ])
    //#endregion
    return (
        <PageContentWrapper>
            {
                <TabView
                    activeIndex={currentTabView.value}
                    onTabChange={e => handleOnchange(e)}
                    className="text-lg">
                    {dataListTabView &&
                        dataListTabView?.length > 0 &&
                        dataListTabView.map(item => (
                            <TabPanel
                                key={item?.key}
                                header={translateKeys(item?.header)}>
                                <div
                                    className="styled-hcp-detail-wrapper"
                                    style={{minHeight: 'calc(100vh - 15rem)'}}>
                                    {item?.component()}
                                </div>
                            </TabPanel>
                        ))}
                </TabView>
            }
        </PageContentWrapper>
    )
}

export default HealthCarePractitionerDetail
